import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer";
import { Vector as VectorSource } from "ol/source";
import { extend, Extent } from "ol/extent";
import { Circle, Fill, Stroke, Style } from "ol/style";
import Feature from "ol/Feature";
import Map from "ol/Map";
import { OlMapStyles } from "@app/view/planning/live-map/ol-map-styles";
import moment from "moment";
import XYZ from "ol/source/XYZ";
import BingMaps from "ol/source/BingMaps";
import {
  Attribution,
  defaults as defaultControls,
  FullScreen,
  ScaleLine
} from "ol/control";
import {
  defaults as defaultInteractions,
  DragRotateAndZoom
} from "ol/interaction";
import Overlay from "ol/Overlay";
import View from "ol/View";
import { fromLonLat } from "ol/proj";
import { EventEmitter } from "@angular/core";
import { ResourceModel } from "@bryntum/scheduler-thin";

export class LiveMapManager {
  public viewExtent: Extent = null;
  public showRouteLayer: boolean = true;
  public routeLayer: VectorLayer<any>;
  public routeSource: VectorSource<any>;
  public showJobLayer: boolean = true;
  public jobLayer: VectorLayer<any>;
  public jobSource: VectorSource<any>;
  public showAvailabilityLayer: boolean = true;
  public availabilityLayer: VectorLayer<any>;
  public availabilitySource: VectorSource<any>;
  public map: Map;
  public selected: any;
  public lastHoveredTickets: number[] = [];
  public styles = OlMapStyles.STYLES;
  public mainView: View;
  public highlightStyles = OlMapStyles.HIGHLIGHT_STYLES;
  ticketHoverChange = new EventEmitter<number[]>();
  displayOverlay: boolean = false;
  public filterResources: any[] = [];
  public tooltipText: string[] = [];
  private overlay: Overlay;

  public applyFilter(resources: ResourceModel[], dateFrom, dateTo) {
    this.filterResources = resources.map((value) => value.id);
    this.filterEvents(dateFrom, dateTo);
  }

  public calculateZoomToFit() {
    let jobExtent = this.calculateSourceExtent(this.jobSource);
    let availabilityExtent = this.calculateSourceExtent(
      this.availabilitySource
    );
    let routeExtent = this.calculateSourceExtent(this.routeSource);
    console.log(
      "calculating zoom to fix with extents",
      jobExtent,
      availabilityExtent,
      routeExtent
    );
    let finalExtent = jobExtent;
    if (finalExtent) {
      if (availabilityExtent) {
        console.log("extending job extent with availability extent");
        extend(finalExtent, availabilityExtent);
      }
    } else {
      finalExtent = availabilityExtent;
    }
    if (finalExtent) {
      if (routeExtent) {
        console.log("extending with route extent");
        extend(finalExtent, routeExtent);
      }
    } else {
      finalExtent = routeExtent;
    }
    if (finalExtent) {
      this.viewExtent = finalExtent;
      this.fitMap();
    }
  }

  fitMap() {
    // console.log('fitting map', this.map, this.viewExtent);
    if (this.map && this.viewExtent) {
      this.map
        .getView()
        .fit(this.viewExtent, { padding: [40, 40, 40, 40], duration: 1000 });
    }
  }

  filterEvents(dateFrom, dateTo) {
    if (this.jobSource) {
      // console.log('filtering events', this.jobSource);
      this.dateFilterSource(this.jobSource, dateFrom, dateTo);
      this.jobSource.dispatchEvent("change");
    }
    if (this.availabilitySource) {
      // console.log('filtering shifts', this.availabilitySource);
      this.dateFilterSource(this.availabilitySource, dateFrom, dateTo);
      this.availabilitySource.dispatchEvent("change");
    }
    if (this.routeSource) {
      // console.log('filtering routes', this.routeSource);
      this.dateFilterSource(this.routeSource, dateFrom, dateTo);
      this.routeSource.dispatchEvent("change");
    }
    // this.map.render();
  }

  public calculateFeatureVisibility(resourceId, start, end, dateFrom, dateTo) {
    if (this.filterResources.length > 0) {
      if (this.filterResources.indexOf(resourceId) < 0) {
        return false;
      }
    }
    return moment(start).isBefore(dateTo) && moment(end).isAfter(dateFrom);
  }

  public calculateSourceExtent(source: VectorSource<any>) {
    let tempExtent = null;
    console.log("assessing source", source);
    let included = [];
    let skipped = [];
    if (source) {
      source.forEachFeature((p1: Feature<any>) => {
        if (p1.get("visible")) {
          included.push(p1.getId());
          if (!tempExtent) {
            tempExtent = p1.getGeometry().getExtent();
          } else {
            extend(tempExtent, p1.getGeometry().getExtent());
          }
        } else {
          skipped.push(p1.getId());
        }
      });
    } else {
      console.log("source not found, cannot calculate extent");
    }
    console.log("included", included);
    console.log("skipped", skipped);
    return tempExtent;
  }

  public postProcess(tempStyle, feature): any {
    // console.log('post processing feature', tempStyle, feature, feature.get('visible'));
    if (!feature.get("visible")) {
      return new Style();
    }
    if (feature.get("type") === "job") {
      let hovered = false;
      if (this.lastHoveredTickets.indexOf(feature.get("id")) !== -1) {
        hovered = true;
      }
      tempStyle.setImage(
        new Circle({
          radius: hovered ? 12 : 10,
          fill: new Fill({ color: feature.get("fillColor") }),
          stroke: new Stroke({ color: feature.get("borderColor"), width: 2 })
        })
      );
      return [tempStyle];
    } else if (feature.get("type") === "home") {
      return [tempStyle];
    }
    return tempStyle;
  }

  public changeRender(bingKey, imagerySet) {
    const layer = this.map.getLayers().item(0);
    console.log(layer);
    this.map.getLayers().removeAt(0);
    this.map.getLayers().insertAt(
      0,
      new TileLayer({
        visible: true,
        preload: Infinity,
        source: new BingMaps({
          key: bingKey,
          imagerySet: imagerySet,
          maxZoom: 19
        })
      })
    );
  }

  public initMap(tileUrl, mapClickCallback, bingKey, imagerySet) {
    this.routeSource = new VectorSource({ features: [] });
    this.routeLayer = new VectorLayer({
      source: this.routeSource,
      style: this.mainStyleFunction.bind(this)
    });

    this.jobSource = new VectorSource({ features: [] });
    this.jobLayer = new VectorLayer({
      source: this.jobSource,
      style: this.mainStyleFunction.bind(this)
    });

    this.availabilitySource = new VectorSource({ features: [] });
    this.availabilityLayer = new VectorLayer({
      source: this.availabilitySource,
      style: this.mainStyleFunction.bind(this)
    });
    this.mainView = new View({
      center: fromLonLat([-2.89479, 54.093409]),
      zoom: 6
    });

    this.recreateMap(tileUrl, bingKey, imagerySet);
    this.map.on("pointermove", this.handlePointerMove.bind(this));
    this.map.on("click", mapClickCallback);
  }

  recreateMap(tileUrl, bingKey, imagerySet) {
    console.log("recreating map");
    const mapLayer = new TileLayer(
      bingKey && bingKey.length > 0
        ? ({
            visible: true,
            preload: Infinity,
            source: new BingMaps({
              key: bingKey,
              imagerySet: imagerySet,
              maxZoom: 19
            })
          } as any)
        : ({
            source: new XYZ({
              attributions: [
                '<a href="https://www.openstreetmap.org/copyright">© OpenStreetMap</a> contributors'
              ],
              url: tileUrl
            })
          } as any)
    );
    this.map = new Map({
      layers: [
        mapLayer,
        this.routeLayer,
        this.availabilityLayer,
        this.jobLayer
      ],
      controls: defaultControls({
        attribution: false
      }).extend([
        new Attribution({
          collapsible: true
        }),
        new FullScreen(),
        new ScaleLine()
      ]),
      interactions: defaultInteractions().extend([new DragRotateAndZoom()]),
      target: "livemap",
      view: this.mainView
    });

    let tooltip = document.getElementById("tooltip");
    this.overlay = new Overlay({
      element: tooltip,
      offset: [10, 0],
      positioning: "bottom-left"
    });
    this.map.addOverlay(this.overlay);
    console.log("layers", this.map.getLayers());
  }

  private dateFilterSource(source: VectorSource<any>, dateFrom, dateTo) {
    // console.log('date filtering source for time range', dateFrom, dateTo);
    source.forEachFeature((feature) => {
      // console.log('checking feature: ', feature);
      feature.set(
        "visible",
        this.calculateFeatureVisibility(
          feature.get("resourceId"),
          feature.get("startDate"),
          feature.get("endDate"),
          dateFrom,
          dateTo
        )
      );
      // console.log('calculated visible', feature.get('visible'));
      this.mainStyleFunction(feature);
    });
  }

  private mainStyleFunction(feature) {
    let tempStyle = this.styles[feature.get("type")];
    return this.postProcess(tempStyle, feature);
  }

  private handlePointerMove(e) {
    let features = [];
    let me = this;
    this.tooltipText = [];
    // this.jobSource.forEachFeature(f => {
    //   me.unhoverFeaturesAtPixel(f);
    // });
    // this.jobSource.refresh();
    this.map.forEachFeatureAtPixel(e.pixel, function (feature) {
      if (feature && features.indexOf(feature) === -1 && feature.get("id")) {
        // feature.changed();

        features.push(feature);
        // me.hoverFeaturesAtPixel(feature);
      }
    });
    // tooltip.style.display = feature ? '' : 'none';
    if (features.length > 0) {
      this.overlay.setPosition(e.coordinate);
      this.displayOverlay = true;
      // tooltip.innerHTML = feature.get('name');
    } else {
      this.displayOverlay = false;
    }
    if (features.length > 0) {
      console.log(features);
      let idMap = [];
      for (const feature of features) {
        if (feature) {
          if (feature.get("type") === "job") {
            me.tooltipText.push(
              feature.get("externalJobReference") +
                " - Arrival " +
                moment(feature.get("arrivedTime")).format("HH:mm") +
                " - " +
                feature.get("resourceName")
            );
            if (feature.get("id") && idMap.indexOf(feature.get("id")) === -1) {
              idMap.push(feature.get("id"));
            }
          }
        }
      }
      console.log("hovered ticket ids", idMap);
      if (this.lastHoveredTickets.length === idMap.length) {
        let changed = false;
        for (let i = 0; i < idMap.length; i++) {
          if (idMap[i] !== this.lastHoveredTickets[i]) {
            changed = true;
          }
        }
        if (changed) {
          this.ticketHoverChange.emit(idMap);
          this.lastHoveredTickets = idMap;
        }
      } else {
        this.ticketHoverChange.emit(idMap);
        this.lastHoveredTickets = idMap;
      }
    } else {
      let unhoveredTickets = [];
      if (this.lastHoveredTickets.length !== 0) {
        console.log("preparing to unhover tickets");
        for (let i = 0; i < this.lastHoveredTickets.length; i++) {
          console.log(
            "preparing to unhover ticket",
            this.lastHoveredTickets[i]
          );
          let feature = this.jobSource.getFeatureById(
            this.lastHoveredTickets[i]
          );
          // this.unhoverFeaturesAtPixel(feature);
          unhoveredTickets.push(feature);
        }
        this.ticketHoverChange.emit([]);
      }
      this.lastHoveredTickets = [];
      for (let i = 0; i < unhoveredTickets.length; i++) {
        let value = unhoveredTickets[i];
        console.log("unhovered feature", value);
        if (value) {
          value.changed();
        }
      }
    }

    // this.map.forEachFeatureAtPixel(e.pixel, this.hoverFeaturesAtPixel.bind(this));
  }
}
